import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import SortingOptionsImage from "./SortingOptionsImage.png"; // Replace with the actual path
import SortedStocksImage from "./SortedStocksImage.png"; // Replace with the actual path
import TableBackground from "./table_back.mp4"; // Video background

const features = [
  {
    title: "Sorting and Filtering Options",
    description:
      "At Stock101, we provide a powerful suite of advanced sorting and filtering tools designed to simplify your stock market analysis. With comprehensive access to the entire NYSE, users can sort stocks using a wide range of parameters, including market capitalization, price, volume, and analyst ratings. Our platform goes beyond basic sorting by enabling additional filtering with various technical data metrics. Whether you’re analyzing option call values, open interest, or searching for stocks tied to specific news events, Stock101 empowers you with unparalleled customization. For those seeking a personalized approach, Stock101 offers the ability to create custom strategies, ensuring you can identify and focus on the most favorable stocks that align with your investment goals.",
    image: SortingOptionsImage,
  },
  {
    title: "Sorted Stocks Display",
    description:
      "At Stock101, we transform your sorting and filtering preferences into actionable insights with our Sorted Stocks Display feature. The entire stock market, filtered and sorted based on your selected criteria, is presented in a clear and concise descending order, highlighting the most relevant stocks for your strategy. The table is designed to deliver all the critical details you need, including price changes, market trends, analyst recommendations, and technical metrics, in one convenient view. This intuitive layout ensures users can quickly evaluate and compare stocks, empowering them to make well-informed and confident investment decisions. With Stock101, staying ahead in the market has never been easier.",
    image: SortedStocksImage,
  },
];

const animationVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, type: "spring" },
  },
};

const TableComponent = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: "50vh",
        fontFamily: "'Work Sans', sans-serif",
        overflow: "hidden",
      }}
    >
      {/* Background Video */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
        }}
      >
        <source src={TableBackground} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Dark Overlay */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: "relative",
          zIndex: 2,
          padding: "80px 20px",
        }}
      >
        {/* Heading */}
        <Typography
          variant="h2"
          sx={{
            fontWeight: "bold",
            fontSize: "3rem",
            textAlign: "center",
            marginBottom: "40px",
            color: "#fff",
            lineHeight: 1.5,
            fontFamily: "'Work Sans', sans-serif",
          }}
        >
          Advanced Sorting and <span style={{ color: "#FF6F00" }}>Organized Stock Display</span>
        </Typography>

        {/* Features Sections */}
        <Box>
          {features.map((feature, index) => (
            <Grid
              container
              spacing={4}
              alignItems="center"
              justifyContent="center"
              key={index}
              sx={{
                marginBottom: "40px",
                flexDirection: index % 2 === 0 ? "row" : "row-reverse", // Alternate layout
              }}
            >
              {/* Text Section */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  whileHover={{ scale: 1.05 }}
                  variants={animationVariants}
                  viewport={{ once: true, amount: 0.5 }}
                  onClick={() => navigate("/screener")}
                  style={{ cursor: "pointer" }}
                >
                  <Box
                    sx={{
                      padding: "20px",
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      borderRadius: "16px",
                      boxShadow: "0 10px 20px rgba(0, 0, 0, 0.2)",
                      "&:hover": {
                        backgroundColor: "#f0f0f0",
                      },
                    }}
                  >
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: "bold",
                        marginBottom: "20px",
                        color: "#333",
                        lineHeight: 1.5,
                        fontFamily: "'Work Sans', sans-serif",
                      }}
                    >
                      {feature.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#666",
                        lineHeight: 1.6,
                        fontFamily: "'Work Sans', sans-serif",
                      }}
                    >
                      {feature.description}
                    </Typography>
                  </Box>
                </motion.div>
              </Grid>

              {/* Image Section */}
              <Grid item xs={12} md={6}>
                <motion.div
                  initial="hidden"
                  whileInView="visible"
                  whileHover={{ scale: 1.05 }}
                  variants={animationVariants}
                  viewport={{ once: true, amount: 0.5 }}
                  onClick={() => navigate("/screener")}
                  style={{ cursor: "pointer" }}
                >
                  <Box
                    component="img"
                    src={feature.image}
                    alt={feature.title}
                    sx={{
                      width: "100%",
                      height: "auto",
                      borderRadius: "16px",
                      boxShadow: "0 10px 20px rgba(0, 0, 0, 0.3)",
                      "&:hover": {
                        transform: "scale(1.02)",
                        transition: "transform 0.3s ease",
                      },
                    }}
                  />
                </motion.div>
              </Grid>
            </Grid>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default TableComponent;
